import {
  Grid,
  Typography,
  Autocomplete,
  TextField,
  IconButton,
  Menu,
  MenuItem,
  CircularProgress,
} from '@mui/material'
import { MoreVert } from '@mui/icons-material'
import { utils, writeFile } from 'xlsx'
import { RecentOrigins } from './components/RecentOrigins'
import { RecentCollections } from './components/RecentCollections'
import {
  Origin,
  ItemCollection,
  useDashboardQuery,
  useItemStatisticsByCollectionTypeQuery,
  useItemCollectionTypesListQuery,
} from '@kistill/client/data-access'
import { ListPage, SelectItem, DateSearch } from '@kistill/client/shared/ui'
import { useState, useEffect } from 'react'
import { useForm } from 'react-hook-form'
import { RadioButtonGroup } from 'react-hook-form-mui'
import { CustomBarChart } from './components/CustomBarChart'
import React from 'react'

export function Dashboard() {
  const { register, control, watch, setValue } = useForm()
  const [anchorEl, setAnchorEl] = React.useState<null | HTMLElement>(null)

  // Set default dates on mount
  useEffect(() => {
    const currentYear = new Date().getFullYear()
    setValue('dateFrom', new Date(currentYear, 0, 1))
    setValue('dateTo', new Date(currentYear, 11, 31))
    setValue('skraninguLokid', 'allir')
  }, [setValue])

  const dateFrom = watch('dateFrom') as Date | undefined
  const dateTo = watch('dateTo') as Date | undefined
  const skraninguLokid = watch('skraninguLokid') as string | undefined

  const [selectedTypes, setSelectedTypes] = useState<SelectItem[]>([])
  const [hasInitialized, setHasInitialized] = useState(false)

  const { data } = useDashboardQuery({
    fetchPolicy: 'network-only',
  })

  const { data: itemCollectionTypeData, loading: itemCollectionTypeLoading } =
    useItemCollectionTypesListQuery()
  const itemCollectionTypeOptions =
    itemCollectionTypeData?.getCollectionTypesList as SelectItem[]

  // Auto-select default types on initial load
  useEffect(() => {
    if (
      itemCollectionTypeOptions &&
      itemCollectionTypeOptions.length > 0 &&
      !hasInitialized
    ) {
      const defaultCodes = [
        'Kr',
        'Kk',
        'Kf',
        'Kc',
        'Kd',
        'Km',
        'Kn',
        'HD',
        'Kv',
        'Ghn',
        'Gdp',
        'Gps',
        'Gpr',
        'L',
        'Lto',
      ]

      const defaultSelected = itemCollectionTypeOptions.filter((option) => {
        // Check if the title starts with any of the default codes followed by ' –' or ' -'
        return defaultCodes.some(
          (code) =>
            option.title.startsWith(`${code} –`) ||
            option.title.startsWith(`${code} -`) ||
            option.title === code,
        )
      })

      if (defaultSelected.length > 0) {
        setSelectedTypes(defaultSelected)
        setHasInitialized(true)
      }
    }
  }, [itemCollectionTypeOptions, hasInitialized])

  const collectionTypeGuids = selectedTypes.map((type) => String(type.id))

  const skraninguLokidValue =
    skraninguLokid === 'ja'
      ? 'true'
      : skraninguLokid === 'nei'
        ? 'false'
        : undefined

  const { data: statsData, loading: statsLoading } =
    useItemStatisticsByCollectionTypeQuery({
      variables: {
        collectionTypeGuids,
        dateFrom: dateFrom?.toISOString() || new Date().toISOString(),
        dateTo: dateTo?.toISOString() || new Date().toISOString(),
        skraninguLokid: skraninguLokidValue,
      },
      fetchPolicy: 'network-only',
      skip: !dateFrom || !dateTo || selectedTypes.length === 0,
    })

  const statistics = statsData?.itemStatisticsByCollectionType?.statistics || []

  const chartColors = [
    '#2C5F8D',
    '#C85A54',
    '#D97B3A',
    '#D4AF37',
    '#5A8F5F',
    '#7B4B94',
    '#b05e7cff',
  ]

  // Prepare Recharts data format
  const chartData = statistics.map((stat, index) => {
    // Extract short code
    const shortCode = stat.collectionTypeName
      .split(' -')[0]
      .split(' –')[0]
      .trim()
    return {
      name: stat.collectionTypeName,
      shortCode: shortCode,
      value: stat.itemCount || 0,
      color: chartColors[index % chartColors.length],
    }
  })

  const openMenu = Boolean(anchorEl)
  const handleClick = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget)
  }
  const handleClose = () => {
    setAnchorEl(null)
  }

  const handleExportToExcel = () => {
    const excelData = statistics.map((stat) => ({
      Safntegund: stat.collectionTypeName,
      Fjöldi: stat.itemCount,
    }))

    // Create worksheet and workbook
    const ws = utils.json_to_sheet(excelData)
    const wb = utils.book_new()
    utils.book_append_sheet(wb, ws, 'Tölfræði')

    // Generate filename
    const yearFrom = dateFrom?.getFullYear() || new Date().getFullYear()
    const yearTo = dateTo?.getFullYear() || new Date().getFullYear()
    const filename =
      yearFrom === yearTo
        ? `tolfraedi_eintok_${yearFrom}.xlsx`
        : `tolfraedi_eintok_${yearFrom}_${yearTo}.xlsx`

    writeFile(wb, filename)
    handleClose()
  }

  return (
    <ListPage title='Forsíða' isBanner={true}>
      <Grid container>
        <Grid item xs={12} py={4} pl={2}>
          <Typography component='h1' variant='h3'>
            Tölfræði
          </Typography>
        </Grid>
        <Grid item container xs={12} pl={2} pr={2} spacing={2}>
          <Grid item xs={12} sm={5} sx={{ minWidth: 300 }}>
            <Autocomplete
              multiple
              size='small'
              options={itemCollectionTypeOptions || []}
              getOptionLabel={(option) => option.title}
              value={selectedTypes}
              onChange={(_, data) => setSelectedTypes(data)}
              loading={itemCollectionTypeLoading}
              renderInput={(params) => (
                <TextField {...params} label='Velja safntegundir' />
              )}
            />
          </Grid>
          <Grid item xs={12} sm={3} sx={{ minWidth: 200 }}>
            <DateSearch
              register={register}
              control={control}
              dateFromLabel='Ár frá'
              dateToLabel='Ár til'
            />
          </Grid>
          <Grid
            item
            xs={12}
            sm={4}
            sx={{
              display: 'flex',
              alignItems: 'flex-start',
              minWidth: 250,
              pr: 0,
            }}
          >
            <Typography
              component='span'
              sx={{ mr: 2, whiteSpace: 'nowrap', mt: 0.5 }}
            >
              Skráningu lokið:
            </Typography>
            <RadioButtonGroup
              name='skraninguLokid'
              control={control}
              options={[
                { id: 'allir', label: 'Allar' },
                { id: 'ja', label: 'Já' },
                { id: 'nei', label: 'Nei' },
              ]}
              row
            />
            <IconButton
              onClick={handleClick}
              disabled={statistics.length === 0}
              sx={{ mt: -0.5 }}
            >
              <MoreVert fontSize='large' />
            </IconButton>
            <Menu anchorEl={anchorEl} open={openMenu} onClose={handleClose}>
              <MenuItem onClick={handleExportToExcel}>
                Sækja excel skjal
              </MenuItem>
            </Menu>
          </Grid>
        </Grid>
        <Grid item xs={12} p={2} sx={{ overflow: 'visible' }}>
          {selectedTypes.length === 0 ? (
            <Typography
              variant='h6'
              color='text.secondary'
              sx={{ textAlign: 'center', py: 4 }}
            >
              Vinsamlegast veldu að minnsta kosti eina safntegund
            </Typography>
          ) : statsLoading ? (
            <Grid container justifyContent='center' py={4}>
              <CircularProgress />
            </Grid>
          ) : statistics.length > 0 ? (
            <CustomBarChart data={chartData} chartColors={chartColors} />
          ) : (
            <Typography
              variant='h6'
              color='text.secondary'
              sx={{ textAlign: 'center', py: 4 }}
            >
              Engin gögn fundust fyrir völd skilyrði
            </Typography>
          )}
        </Grid>
        <Grid item xs={12} p={2}>
          <Typography component='h1' variant='h3'>
            Nýskráningar
          </Typography>
        </Grid>
        <Grid item container xs={12} pl={2} pt={2} pr={2} direction='column'>
          <Grid item>
            <RecentOrigins data={data?.origins?.slice(0, 5) as Origin[]} />
          </Grid>
          <Grid item>
            <RecentCollections
              data={data?.itemCollections?.slice(0, 5) as ItemCollection[]}
            />
          </Grid>
        </Grid>
      </Grid>
    </ListPage>
  )
}
